import { AdminOnboardingDistribution } from '@orbit/api-types'
import React from 'react'
import { barWidth, formatShare } from './onboardingView'
import {
	BarFill,
	BarLabel,
	BarRow,
	BarTrack,
	BarValue,
	Section,
	SectionSubtitle,
	SectionTitle,
	UnknownTag,
} from './OnboardingPage.styles'
import { EmptyNoteInline } from '@components/EmptyState'

interface DistributionBarsProps {
	title: string
	subtitle: string
	distribution: AdminOnboardingDistribution
}

/**
 * One survey question's answer distribution as horizontal bars.
 *
 * Bars are scaled against the busiest option, while the number beside each is a
 * share of the users who answered *this* question — so a multi-select question's
 * shares can sum past 100% without the bars overflowing the row. Options the
 * shared enum does not carry are flagged rather than blended in, so a client
 * inventing a value shows up instead of quietly inflating a total.
 */
const DistributionBars: React.FC<DistributionBarsProps> = ({ title, subtitle, distribution }) => {
	const max = Math.max(0, ...distribution.options.map(option => option.count))

	return (
		<Section>
			<SectionTitle>{title}</SectionTitle>
			<SectionSubtitle>{subtitle}</SectionSubtitle>

			{distribution.options.length === 0 ? (
				<EmptyNoteInline>No answers in this range.</EmptyNoteInline>
			) : (
				distribution.options.map(option => (
					<BarRow key={option.option}>
						<BarLabel $unknown={option.isUnknown} title={option.option}>
							{option.option}
							{option.isUnknown && <UnknownTag>new</UnknownTag>}
						</BarLabel>
						<BarTrack>
							<BarFill $width={barWidth(option.count, max)} $unknown={option.isUnknown} />
						</BarTrack>
						<BarValue>
							{option.count.toLocaleString()} · {formatShare(option.count, distribution.answered)}
						</BarValue>
					</BarRow>
				))
			)}
		</Section>
	)
}

export default DistributionBars
